import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/Users.css';
import FilterDropdown from '../component/Usercomponents/Filteruser.jsx';
import ShowAddModal from '../component/Usercomponents/Adduser.jsx';
import useApi from '../../hooks/useApi.js';
import { toast } from 'react-toastify';
import { Modal, Button, Form, Badge, InputGroup } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';

const Users = () => {
  const { getUsers, deleteUser, loading, error } = useApi();
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('single');
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch users on component mount and when a new user is added
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getUsers();
        setUsers(data);
      } catch (err) {
        // Error is handled in useApi with toast
      }
    };
    fetchUsers();
  }, [getUsers]);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.userName?.toLowerCase().includes(searchLower) ||
      user.phoneNumber?.includes(searchTerm) ||
      user.emailId?.toLowerCase().includes(searchLower)
    );
  });

  // Handle modal close and refresh users
  const handleModalClose = () => {
    setShowAddContactModal(false);
    setIsEdit(false);
    setUserToEdit(null);
    getUsers().then((data) => setUsers(data)).catch(() => {});
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (userId) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Handle edit user
  const handleEdit = () => {
    if (selectedUsers.size !== 1) {
      toast.warn('Please select exactly one user to edit.');
      return;
    }
    const userId = Array.from(selectedUsers)[0];
    const user = users.find((u) => u.userId === userId);
    if (user) {
      setIsEdit(true);
      setUserToEdit(user);
      setActiveTab('single');
      setShowAddContactModal(true);
    } else {
      toast.error('Selected user not found.');
    }
  };

  // Handle delete selected users
  const handleDeleteSelected = async () => {
    if (selectedUsers.size === 0) {
      toast.warn('Please select at least one user to delete.');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedUsers.size} user(s)?`)) {
      try {
        await Promise.all(
          Array.from(selectedUsers).map(async (userId) => {
            await deleteUser(userId);
          })
        );
        const data = await getUsers();
        setUsers(data);
        setSelectedUsers(new Set());
        toast.success(`${selectedUsers.size} user(s) deleted successfully.`);
      } catch (err) {
        // Error is handled in useApi with toast
      }
    }
  };

  // Handle export modal
  const handleExport = () => {
    if (users.length === 0) {
      toast.warn('No users available to export.');
      return;
    }
    setShowExportModal(true);
  };

  const handleExportFormatChange = (e) => {
    setExportFormat(e.target.value);
  };

  const handleExportSubmit = async () => {
    try {
      const exportData = users.map((user) => ({
        userId: user.userId || '',
        userName: user.userName || '',
        emailId: user.emailId || '',
        phoneNumber: user.phoneNumber || '',
        optedIn: user.optedIn != null ? user.optedIn.toString() : '',
        createdAt: user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '',
        updatedAt: user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : '',
      }));

      if (exportFormat === 'csv') {
        const csv = Papa.unparse(exportData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'users.csv');
      } else if (exportFormat === 'xlsx') {
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, 'users.xlsx');
      } else if (exportFormat === 'docx') {
        const doc = new Document({
          sections: [
            {
              properties: {},
              children: [
                new Paragraph({
                  text: 'Users Data',
                  heading: 'Heading1',
                  alignment: 'center',
                  spacing: { after: 200 },
                }),
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({ children: [new Paragraph('User ID')], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                        new TableCell({ children: [new Paragraph('Name')], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                        new TableCell({ children: [new Paragraph('Email')], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                        new TableCell({ children: [new Paragraph('Phone')], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                        new TableCell({ children: [new Paragraph('Opted In')], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                        new TableCell({ children: [new Paragraph('Created On')], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                        new TableCell({ children: [new Paragraph('Updated On')], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                      ],
                    }),
                    ...exportData.map(
                      (user) =>
                        new TableRow({
                          children: [
                            new TableCell({ children: [new Paragraph(user.userId)], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                            new TableCell({ children: [new Paragraph(user.userName)], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                            new TableCell({ children: [new Paragraph(user.emailId)], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                            new TableCell({ children: [new Paragraph(user.phoneNumber)], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                            new TableCell({ children: [new Paragraph(user.optedIn)], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                            new TableCell({ children: [new Paragraph(user.createdAt)], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                            new TableCell({ children: [new Paragraph(user.updatedAt)], margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
                          ],
                        })
                    ),
                  ],
                }),
              ],
            },
          ],
        });

        try {
          const blob = await Packer.toBlob(doc);
          saveAs(blob, 'users.docx');
        } catch (docxError) {
          console.error('DOCX generation error:', docxError);
          throw new Error('Failed to generate DOCX file');
        }
      }
      toast.success(`Users exported successfully as ${exportFormat.toUpperCase()}.`);
      setShowExportModal(false);
    } catch (err) {
      console.error('Export error:', err);
      toast.error(`Failed to export users: ${err.message}`);
    }
  };

  return (
    <div className="users-management-container">
      {/* Header Section */}
      <div className="header-section mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="fw-bold mb-1">
              <i className="bi bi-people-fill me-2"></i>Contacts Management
            </h4>
            <p className="text-muted small mb-0">Manage your WhatsApp Business contacts</p>
          </div>
          <button
            className="btn btn-success btn-lg"
            onClick={() => {
              setIsEdit(false);
              setUserToEdit(null);
              setShowAddContactModal(true);
            }}
          >
            <i className="bi bi-person-plus me-2"></i> Add Contact
          </button>
        </div>
      </div>

      {/* Toolbar Section */}
      <div className="toolbar-section mb-4 p-3 bg-light rounded-3 shadow-sm">
        <div className="row g-3">
          <div className="col-md-6">
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name, phone or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                Clear
              </Button>
            </InputGroup>
          </div>
          {/* <div className="col-md-6">
            <div className="d-flex justify-content-end gap-2">
              <FilterDropdown />
            </div>
          </div> */}
        </div>
      </div>

      {/* Action Bar */}
      <div className="action-bar mb-4 p-3 bg-white rounded-3 shadow-sm">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center me-3">
            <Form.Check
              type="checkbox"
              id="select-all-users"
              label={`${selectedUsers.size} selected`}
              className="me-2"
              onChange={() => {
                if (selectedUsers.size === filteredUsers.length) {
                  setSelectedUsers(new Set());
                } else {
                  setSelectedUsers(new Set(filteredUsers.map((user) => user.userId)));
                }
              }}
              checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
            />
            <Badge bg="light" text="dark" className="ms-2">
              Total: {filteredUsers.length}
            </Badge>
          </div>

          <div className="vr"></div>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleExport}
            disabled={filteredUsers.length === 0}
          >
            <i className="bi bi-download me-1"></i> Export
          </Button>

          <Button
            variant="outline-warning"
            size="sm"
            onClick={handleEdit}
            disabled={selectedUsers.size !== 1}
          >
            <i className="bi bi-pencil me-1"></i> Edit
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleDeleteSelected}
            disabled={selectedUsers.size === 0}
          >
            <i className="bi bi-trash me-1"></i> Delete
          </Button>
        </div>
      </div>

      {/* User Table */}
      <div className="table-section">
        <div className="table-responsive rounded-3 shadow-sm">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    onChange={() => {
                      if (selectedUsers.size === filteredUsers.length) {
                        setSelectedUsers(new Set());
                      } else {
                        setSelectedUsers(new Set(filteredUsers.map((user) => user.userId)));
                      }
                    }}
                    checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                  />
                </th>
                <th>
                  <i className="bi bi-person-fill me-2"></i> Contact
                </th>
                <th>
                  <i className="bi bi-telephone-fill me-2"></i> Phone
                </th>
                <th>
                  <i className="bi bi-envelope-fill me-2"></i> Email
                </th>
                <th>
                  <i className="bi bi-calendar-event-fill me-2"></i> Created
                </th>
                <th>
                  <i className="bi bi-tags-fill me-2"></i> Status
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <div className="d-flex justify-content-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" className="text-center text-danger py-4">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    Error loading contacts: {error}
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.userId} className={selectedUsers.has(user.userId) ? 'table-active' : ''}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsers.has(user.userId)}
                        onChange={() => handleCheckboxChange(user.userId)}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar me-3">
                          <i className="bi bi-person-circle fs-4"></i>
                        </div>
                        <div>
                          <div className="fw-medium">{user.userName || 'No name'}</div>
                          <div className="text-muted small">ID: {user.userId}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {user.phoneNumber ? (
                        <a href={`tel:${user.phoneNumber}`} className="text-decoration-none">
                          {user.phoneNumber}
                        </a>
                      ) : (
                        <span className="text-muted">Not provided</span>
                      )}
                    </td>
                    <td>
                      {user.emailId ? (
                        <a href={`mailto:${user.emailId}`} className="text-decoration-none">
                          {user.emailId}
                        </a>
                      ) : (
                        <span className="text-muted">Not provided</span>
                      )}
                    </td>
                    <td>
                      <div className="small">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                      </div>
                    </td>
                    <td>
                      <Badge bg={user.optedIn ? 'success' : 'secondary'}>
                        {user.optedIn ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <div className="text-muted">
                      <i className="bi bi-people me-2"></i>
                      {searchTerm ? 'No matching contacts found' : 'No contacts available'}
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-2"
                      onClick={() => setShowAddContactModal(true)}
                    >
                      <i className="bi bi-person-plus me-1"></i> Add First Contact
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Contact Modal */}
      <ShowAddModal
        showAddContactModal={showAddContactModal}
        setShowAddContactModal={setShowAddContactModal}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onClose={handleModalClose}
        isEdit={isEdit}
        userToEdit={userToEdit}
      />

      {/* Export Data Modal */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header className="bg-info text-white">
          <Modal.Title>
            <i className="bi bi-download me-2"></i>
            Export Contacts
          </Modal.Title>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => setShowExportModal(false)}
          />
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label>Select Export Format</Form.Label>
              <Form.Select
                value={exportFormat}
                onChange={handleExportFormatChange}
                className="mb-3"
              >
                <option value="csv">CSV (Comma Separated Values)</option>
                <option value="xlsx">Excel Workbook (XLSX)</option>
                <option value="docx">Word Document (DOCX)</option>
              </Form.Select>
              <div className="alert alert-light">
                <i className="bi bi-info-circle me-2"></i>
                All {users.length} contacts will be exported in the selected format.
              </div>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </Button>
              <Button variant="info" onClick={handleExportSubmit}>
                <i className="bi bi-download me-2"></i>
                Export Now
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Users;